import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, setToken } from '../utils/api';
import HowItWorksModal from '../components/HowItWorksModal';

const FantasyLeagueAuth = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('register');
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSent, setForgotSent] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        if (activeTab === 'register' && !formData.fullName.trim()) {
            newErrors.fullName = 'Full name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8 || !/\d/.test(formData.password)) {
            newErrors.password = 'Min. 8 chars, must include a number';
        }
        if (activeTab === 'register' && formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
        setApiError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        setApiError('');

        try {
            let response;

            if (activeTab === 'register') {
                const [first_name, ...rest] = formData.fullName.trim().split(' ');
                const last_name = rest.join(' ') || first_name;
                response = await api.register({
                    email: formData.email,
                    password: formData.password,
                    first_name,
                    last_name,
                });
            } else {
                response = await api.login({
                    email: formData.email,
                    password: formData.password,
                });
            }

            // Store the JWT and user info
            setToken(response.access_token);
            localStorage.setItem('user_role', response.role);
            localStorage.setItem('user_first_name', response.first_name);

            navigate('/dashboard');
        } catch (err) {
            setApiError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTabSwitch = (tab) => {
        setActiveTab(tab);
        setErrors({});
        setApiError('');
        setForgotEmail('');
        setForgotSent(false);
        setFormData({ fullName: '', email: '', password: '', confirmPassword: '' });
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        if (!forgotEmail.trim()) return;
        setIsSubmitting(true);
        setApiError('');
        try {
            await api.forgotPassword(forgotEmail.trim());
            setForgotSent(true);
        } catch (err) {
            setApiError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050d1f] text-white flex flex-col items-center p-4 sm:p-8 relative overflow-hidden font-sans">
            {/* bg glows */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#4d7cff]/10 rounded-full blur-[120px]" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#4d7cff]/5 rounded-full blur-[100px]" />

            {/* nav */}
            <div className="w-full max-w-7xl flex justify-between items-center mb-6 sm:mb-12 z-10">
                <h1 className="text-2xl sm:text-3xl font-bold text-[#4d7cff] tracking-tight">PlayerIQ</h1>
                <div className="flex gap-4 sm:gap-8 text-sm font-medium text-white/70">
                    <button onClick={() => setShowHowItWorks(true)} className="hover:text-white transition-colors">How it works</button>
                    <button className="hover:text-white transition-colors hidden sm:block">Pricing</button>
                </div>
            </div>

            <div className="max-w-7xl w-full grid lg:grid-cols-2 gap-8 lg:gap-20 items-center z-10 mt-2 sm:mt-10">
                {/* hero section — hidden on mobile, shown on lg+ */}
                <div className="lg:pr-10 hidden lg:block">
                    <h2 className="text-[#4d7cff] text-lg font-semibold mb-5 tracking-widest uppercase">AI-Powered Analysis</h2>
                    <h1 className="text-5xl xl:text-7xl font-bold leading-[1.1] mb-8 -tracking-tight">Dominate Your<br /><span className="text-[#4d7cff]">Fantasy League</span></h1>
                    <p className="text-white/50 text-xl leading-relaxed mb-12 max-w-lg">Advanced NFL player projections powered by machine learning.</p>
                    <ul className="space-y-3">
                        {[
                            'Head-to-head matchup analysis',
                            'AI-powered player projections & rankings',
                            'Real-time updates',
                            'Advanced statistical visualizations',
                        ].map((item) => (
                            <li key={item} className="flex items-center gap-3 text-white/70">
                                <span className="w-5 h-5 rounded-full bg-[#4d7cff]/20 border border-[#4d7cff]/50 flex items-center justify-center flex-shrink-0">
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path d="M2 5L4 7L8 3" stroke="#4d7cff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* auth card */}
                <div className="relative">
                    <div className="absolute -inset-0.5 bg-[#4d7cff]/20 rounded-[32px] blur-sm"></div>
                    <div className="relative bg-[#0c1526]/95 backdrop-blur-2xl border border-white/[0.08] rounded-[24px] sm:rounded-[32px] p-6 sm:p-10">

                        {/* tabs — hidden on forgot-password view */}
                        {activeTab !== 'forgot' && (
                            <div className="flex gap-3 mb-10 bg-white/10 p-1.5 rounded-full">
                                <button
                                    onClick={() => handleTabSwitch('register')}
                                    className={`flex-1 py-3.5 px-6 rounded-full text-base font-semibold transition-all duration-300 ${
                                        activeTab === 'register' ? 'bg-[#4d7cff] text-white shadow-lg' : 'bg-transparent text-white/60'
                                    }`}>
                                    Register
                                </button>
                                <button
                                    onClick={() => handleTabSwitch('login')}
                                    className={`flex-1 py-3.5 px-6 rounded-full text-base font-semibold transition-all duration-300 ${
                                        activeTab === 'login' ? 'bg-[#4d7cff] text-white shadow-lg' : 'bg-transparent text-white/60'
                                    }`}>
                                    Login
                                </button>
                            </div>
                        )}

                        {/* API-level error banner */}
                        {apiError && (
                            <div className="mb-6 px-5 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                                {apiError}
                            </div>
                        )}

                        {/* ── Forgot password view ── */}
                        {activeTab === 'forgot' ? (
                            <div className="space-y-6">
                                <div className="mb-6">
                                    <h3 className="text-xl font-bold mb-1">Reset your password</h3>
                                    <p className="text-white/50 text-sm">Enter your email and we'll send you a reset link.</p>
                                </div>
                                {forgotSent ? (
                                    <div className="px-5 py-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 text-sm text-center">
                                        Check your inbox — a reset link is on its way.
                                    </div>
                                ) : (
                                    <form onSubmit={handleForgotSubmit} className="space-y-6">
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            className="w-full bg-white/10 rounded-full px-7 py-4 text-white outline-none focus:ring-2 focus:ring-[#4d7cff]/30"
                                            required
                                        />
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-[#4d7cff] rounded-full px-7 py-4 text-white text-lg font-bold shadow-[0_8px_24px_rgba(77,124,255,0.3)] hover:bg-[#3d6cef] transition-all disabled:opacity-70"
                                        >
                                            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                                        </button>
                                    </form>
                                )}
                                <p className="text-center text-white/40 text-sm">
                                    <button type="button" onClick={() => handleTabSwitch('login')} className="text-[#4d7cff] hover:underline">
                                        Back to login
                                    </button>
                                </p>
                            </div>
                        ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {activeTab === 'register' && (
                                <div className="flex flex-col gap-2">
                                    <input
                                        type="text"
                                        name="fullName"
                                        placeholder="Full Name"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        className={`w-full bg-white/10 rounded-full px-7 py-4 text-white outline-none focus:ring-2 focus:ring-[#4d7cff]/30 ${errors.fullName ? 'ring-2 ring-red-500' : ''}`}
                                    />
                                    {errors.fullName && <span className="text-red-500 text-sm pl-5">{errors.fullName}</span>}
                                </div>
                            )}

                            <div className="flex flex-col gap-2">
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`w-full bg-white/10 rounded-full px-7 py-4 text-white outline-none focus:ring-2 focus:ring-[#4d7cff]/30 ${errors.email ? 'ring-2 ring-red-500' : ''}`}
                                />
                                {errors.email && <span className="text-red-500 text-sm pl-5">{errors.email}</span>}
                            </div>

                            <div className="flex flex-col gap-2">
                                <input
                                    type="password"
                                    name="password"
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`w-full bg-white/10 rounded-full px-7 py-4 text-white outline-none focus:ring-2 focus:ring-[#4d7cff]/30 ${errors.password ? 'ring-2 ring-red-500' : ''}`}
                                />
                                {errors.password && <span className="text-red-500 text-sm pl-5">{errors.password}</span>}
                            </div>

                            {activeTab === 'register' && (
                                <div className="flex flex-col gap-2">
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        placeholder="Confirm Password"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className={`w-full bg-white/10 rounded-full px-7 py-4 text-white outline-none focus:ring-2 focus:ring-[#4d7cff]/30 ${errors.confirmPassword ? 'ring-2 ring-red-500' : ''}`}
                                    />
                                    {errors.confirmPassword && <span className="text-red-500 text-sm pl-5">{errors.confirmPassword}</span>}
                                </div>
                            )}

                            {activeTab === 'login' && (
                                <div className="text-center -mt-3">
                                    <button type="button" onClick={() => navigate('/forgot-password')} className="text-[#4d7cff] text-sm font-medium hover:underline">
                                        Forgot password?
                                    </button>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-[#4d7cff] rounded-full px-7 py-4 text-white text-lg font-bold shadow-[0_8px_24px_rgba(77,124,255,0.3)] hover:bg-[#3d6cef] transition-all disabled:opacity-70"
                            >
                                {isSubmitting ? 'Processing...' : (activeTab === 'register' ? 'Register' : 'Login')}
                            </button>

                            {activeTab === 'register' && (
                                <p className="text-center text-white/40 text-sm">
                                    Already a member?{' '}
                                    <button type="button" onClick={() => handleTabSwitch('login')} className="text-[#4d7cff] hover:underline">
                                        Sign in
                                    </button>
                                </p>
                            )}
                        </form>
                        )}
                    </div>
                </div>
            </div>

            {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)} />}
        </div>
    );
};

export default FantasyLeagueAuth;
